import React, { useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import closeLogoBlack from "../assets/images/chats/closeLogoBlack.svg";
import { CloseButton, CloseIcon } from "../page/Profille/CustomDrawer";
import profile_background_logo from "../assets/images/Network.svg";
import linkedin from "../assets/images/postcard/Linkedin.svg";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import PersonIcon from "../assets/images/chats/person.svg";
import { createStyledIndicatorButton } from "./StyledComponents/SideBarComponents";
import ProfileOwnerDetailModal from "./ProfileOwnerDetailModal";
import group_icon from "../assets/images/chats/group_icon.svg";
import useAppSelectors from "../customHooks/useAppSelectors";
import moment from "moment";


const style = {
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "323px",
  bgcolor: "background.paper",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.30)",
};

const PictureBox = ({ profileImgUrl, coverImgUrl, initialName = "" }) => (
  <Box maxHeight="8rem" height="8rem" width="100%" position="relative">
    <Box height="100%" overflow="hidden">
      <img
        src={coverImgUrl || profile_background_logo}
        alt="coverImg"
        style={{ width: "100%", height: "100%" }}
      />
    </Box>

    <Box
      bgcolor="#EAEAEA"
      position="absolute"
      left="50%"
      marginLeft="-50px"
      transform="translateX(-40%)"
      bottom="-20%"
      borderRadius="0.2rem"
      overflow="hidden"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100px"
        width="100px"
        padding={profileImgUrl ? "0px" : "15px"}
      >
        {profileImgUrl ? (
          <img
            src={profileImgUrl}
            alt="profileImg"
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
          />
        ) : (
          <Typography fontSize="1.8rem">
            {initialName && initialName[0]}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

const ProfileDetailModal = ({
  isProfileViewOpen,
  setProfileViewModal,
  profileViewData,
  setProfileViewData,
}) => {
  const { t } = useTranslation();
  const appContent = t("AppContent", { returnObjects: true });
  const SmallActionButton = createStyledIndicatorButton("#e6e3e3ff");
  const { currentUserData } = useAppSelectors();

  const [isProfileOwnerOpen, setIsProfileOwnerOpen] = useState(false);
  const [viewType, setViewType] = useState("owner");

  const handleClose = () => {
    setProfileViewModal(false);
    setProfileViewData("");
  };  

  // open owner or group modal dynamically
  const handleOwnerDetail = (type) => {
    setViewType(type); // "owner" or "group"
    setIsProfileOwnerOpen(true);
  };

  const ownerDetails = profileViewData?.ownerData || {
    companyName: profileViewData?.companyName,
    cityName: profileViewData?.cityName,
    countryName: profileViewData?.countryName,
    industryName: profileViewData?.industry,
    website: profileViewData?.website,
    linkedin: profileViewData?.linkedin,
    coverImgUrl: profileViewData?.coverImgUrl,
    profileImgUrl: profileViewData?.profileImgUrl,
  };

  const groupDetails = profileViewData?.groupData || {
    userName: profileViewData?.groupOwner,
    email: profileViewData?.email,
  };

  return (
    <div>
      {/* Shared owner/group detail modal */}
      <ProfileOwnerDetailModal
        ownerData={ownerDetails}
        groupData={groupDetails}
        viewType={viewType}
        open={isProfileOwnerOpen}
        onClose={() => setIsProfileOwnerOpen(false)}
      />

      <Modal open={isProfileViewOpen} onClose={handleClose}>
        {profileViewData?.isGroup ? (
          <Box sx={style} textAlign="center">
            {/* Header */}
            <Box
              display="flex"
              alignItems="center"
              gap="0.4rem"
              padding="0.5rem"
            >
              <CloseButton
                onClick={handleClose}
                size="small"
                variant="contained"
              >
                <CloseIcon src={closeLogoBlack} alt="close" />
              </CloseButton>

              <Typography color="#000" fontSize="1rem" fontWeight={"16px"}>
                {"Group Information"}
              </Typography>
            </Box>

            {/* Group Info */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              padding="20px"
              fontFamily="Inter, sans-serif"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                height="52px"
                width="52px"
                p="12px"
                sx={{
                  backgroundColor: "rgba(243, 243, 243, 1)",
                  borderRadius: "7px",
                  objectFit: "contain",
                  boxShadow: "0px 2.27px 5.27px 0px rgba(0, 0, 0, 0.25)",
                }}
              >
                <img
                  src={group_icon}
                  alt="profileImg"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>

              <Typography fontWeight={600} fontSize="16px" mt={1.3}>
                {profileViewData?.groupName}
              </Typography>

              <Typography fontSize="14px" mt={0.5} mb={1}>
                {profileViewData?.memberCount} Members
              </Typography>

              <Typography fontSize="14px" mb={2} lineHeight={1.4}>
                {profileViewData?.description ||
                  "A chat group to explore Arkchat features and collaboration tools."}
              </Typography>

              {/* Group Owner + Creator Info */}
              <Box
                alignItems="center"
                justifyContent="flex-start"
                gap={3}
                mb={1.5}
                ml={-5}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <SmallActionButton
                    size="small"
                    variant="contained"
                    sx={{ color: "black", width: "28px" }}
                    onClick={() => handleOwnerDetail("owner")}
                  >
                    {profileViewData?.groupOwner
                      ? profileViewData.groupOwner.charAt(0).toUpperCase()
                      : "?"}
                  </SmallActionButton>

                  <Typography
                    fontSize="13px"
                    textAlign="left"
                    fontFamily="Poppins"
                  >
                    Group Owner: {profileViewData?.companyName || "-"}
                  </Typography>
                </Box>

                <Box display="flex" gap={1} mt={1}>
                  <SmallActionButton
                    size="small"
                    variant="contained"
                    onClick={() => handleOwnerDetail("group")}
                    sx={{ width: "28px" }}
                  >
                    <img
                      style={{ width: "100%", height: "100%" }}
                      src={PersonIcon}
                      alt="group"
                    />
                  </SmallActionButton>

                  <Typography
                    fontSize="13px"
                    textAlign="left"
                    fontFamily="Poppins"
                  >
                    Created by {profileViewData?.groupOwner || "-"} <br />
                    {moment(profileViewData?.createdDate).format(currentUserData?.userDateFormat) || "-"}
                  </Typography>
                </Box>
              </Box>

              <Typography fontSize="13px" mb={1}>
                You joined this group on {moment(profileViewData?.joinedDate).format(currentUserData?.userDateFormat)}
              </Typography>
            </Box>
          </Box>
        ) : (
          // User Profile Layout
          <Box sx={style} overflow="hidden">
            <Box
              display="flex"
              alignItems="center"
              gap="0.4rem"
              bgcolor="rgba(234, 234, 234, 1)"
              padding="0.5rem"
            >
              <CloseButton
                onClick={handleClose}
                size="small"
                variant="contained"
              >
                <CloseIcon src={closeLogoBlack} alt="close" />
              </CloseButton>

              <Typography color="#000" fontSize="1rem" fontWeight={600}>
                {appContent?.KEY14 || "Profile"}
              </Typography>
            </Box>

            {!profileViewData?.userName && (
              <Box
                zIndex={2011}
                position="absolute"
                display="flex"
                alignItems="center"
                height="95%"
                width="100%"
                justifyContent="center"
                bgcolor="rgba(0, 0, 0, 0.5)"
              >
                <CircularProgress sx={{ color: "#DB0011" }} />
              </Box>
            )}

            <Box position="relative">
              <PictureBox
                profileImgUrl={profileViewData?.profileImgUrl}
                coverImgUrl={profileViewData?.coverImgUrl}
                initialName={profileViewData?.userName}
              />

              {profileViewData?.linkedin && (
                <Box
                  sx={{ cursor: "pointer" }}
                  position="absolute"
                  right="8rem"
                  bottom="-1.6rem"
                >
                  <Link
                    href={
                      profileViewData.linkedin.startsWith("http")
                        ? profileViewData.linkedin
                        : `https://${profileViewData.linkedin}`
                    }
                    target="_blank"
                    rel="noopener"
                  >
                    <img src={linkedin} title="Linkedin" alt="linkedin" />
                  </Link>
                </Box>
              )}
            </Box>

            <Box mt="2rem" textAlign="center" padding="0.5rem 0" mb="1rem">
              <Typography fontWeight={600}>
                {profileViewData?.userName}
              </Typography>
              <Typography>{profileViewData?.companyName}</Typography>

              {profileViewData?.website && (
                <Link
                  href={
                    profileViewData.website.startsWith("http")
                      ? profileViewData.website
                      : `https://${profileViewData.website}`
                  }
                  target="_blank"
                  rel="noopener"
                >
                  <Typography
                    fontSize="0.88rem"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    {profileViewData.website}
                  </Typography>
                </Link>
              )}

              <Typography>
                {profileViewData?.industryName} <br /> {profileViewData?.cityName}, {profileViewData?.countryName}
              </Typography>

              {profileViewData?.profileBrief && (
                <Box mt="1rem" margin="auto">
                  <Typography sx={{ wordBreak: "break-word" }} width="100%">
                    {profileViewData.profileBrief}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default ProfileDetailModal;
